import { type FC, type FocusEvent, type KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { _isNil, _notNil } from '@/littledash';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import type { SubEditorProps } from '@/components/UI/DataTableV2/SingleCellEditor/SingleCellEditor';
import { isDateFieldType } from '@/components/UI/DataTableV2/DataTable.util';
import type { PickerDisplayFormat } from './pickerFormat';
import { PickerDisplayFormat as PickerFormat } from './pickerFormat';
import styles from './DateCellEditor.module.scss';

/**
 * Resolves the date picker display format and auto-insert value for the column.
 */
const resolveDateFormat = (
  columnType: string | undefined,
  metadataFieldType: string | undefined
): {
  inputType: 'date' | 'datetime-local';
  displayFormat: PickerDisplayFormat;
  includeSeconds: boolean;
  getAutoInsertValue: () => string;
} => {
  const isMetadataDate = columnType === 'metadata' && isDateFieldType(metadataFieldType);

  if (isMetadataDate) {
    return {
      inputType: 'date',
      displayFormat: PickerFormat.Date,
      includeSeconds: false,
      getAutoInsertValue: () => DateUtils.dateNow(),
    };
  }

  const includeSeconds =
    columnType === 'timestamp' ||
    columnType === 'timestampBaseline' ||
    columnType === 'sampleCollectedAt' ||
    columnType === 'sampleDoseTime';

  return {
    inputType: 'datetime-local',
    displayFormat: includeSeconds ? PickerFormat.DateTimeHoursMinutesSeconds : PickerFormat.DateTimeHoursMinutes,
    includeSeconds,
    getAutoInsertValue: () => DateUtils.dateTimeNow(),
  };
};

/**
 * Native date/datetime picker editor for date/timestamp columns.
 *
 * Renders `<input type="date">` for metadata date fields or `<input type="datetime-local">`
 * for timestamp/timestampBaseline/sampleCollectedAt/sampleDoseTime columns. On mount:
 * - Empty cell + no content → auto-insert now() + validate + navigate (no picker)
 * - Otherwise → seed from cell value or now(), open picker + focus
 *
 * Commits via onCommit with `validate: true` so the shell triggers validation after
 * updateCells. Clears to null on empty input. Guards escape/blur race with finishedRef.
 */
export const DateCellEditor: FC<SubEditorProps> = ({
  idCoordinate,
  indexCoordinate,
  content,
  direction,
  onCommit,
  onCancel,
}) => {
  const { dataTableService } = useDataTable();
  const inputRef = useRef<HTMLInputElement>(null);
  const finishedRef = useRef(false);

  const column = dataTableService.columnByIndex(indexCoordinate.column);
  const { inputType, displayFormat, includeSeconds, getAutoInsertValue } = resolveDateFormat(
    column?.type,
    column?.type === 'metadata' ? column.metadata?.field_type : undefined
  );

  const isDateOnly = displayFormat === PickerFormat.Date;

  const [cellValue] = useState(() => {
    return dataTableService.cellData(idCoordinate)?.value ?? null;
  });

  const shouldAutoInsert = _isNil(cellValue) && _isNil(content);

  useEffect(() => {
    if (shouldAutoInsert) {
      const autoInsertValue = getAutoInsertValue();
      onCommit(autoInsertValue, {
        moveSelection: direction ?? 'DOWN',
        validate: true,
      });
    }
  }, [shouldAutoInsert, getAutoInsertValue, onCommit, direction]);

  useLayoutEffect(() => {
    if (shouldAutoInsert || _isNil(inputRef.current)) {
      return;
    }

    const input = inputRef.current;

    if (isDateOnly) {
      const dateValue = _notNil(cellValue) && DateUtils.isValidDate(cellValue) ? cellValue : DateUtils.dateNow();
      input.value = dateValue;
    } else {
      const isoValue = _notNil(cellValue) && DateUtils.isValidDateTime(cellValue) ? cellValue : DateUtils.dateTimeNow();
      input.value = DateInputUtils.toLocalDateTime(isoValue, { includeSeconds });
    }

    requestAnimationFrame(() => {
      input.showPicker?.();
      input.focus();
    });
  }, [shouldAutoInsert, cellValue, isDateOnly, includeSeconds]);

  const commitValue = (): void => {
    if (_isNil(inputRef.current)) {
      return;
    }

    const input = inputRef.current;
    const rawValue = input.value;

    const persistValue = isDateOnly
      ? DateUtils.isValidDate(rawValue)
        ? rawValue
        : ''
      : DateInputUtils.localDateTimeToISODateTime(rawValue);

    const finalValue = persistValue === '' ? null : persistValue;

    if (finalValue === cellValue) {
      onCancel();
      return;
    }

    onCommit(finalValue, { validate: true });
  };

  const commitValueWithMove = (moveDirection: 'UP' | 'DOWN'): void => {
    if (_isNil(inputRef.current)) {
      return;
    }

    const input = inputRef.current;
    const rawValue = input.value;

    const persistValue = isDateOnly
      ? DateUtils.isValidDate(rawValue)
        ? rawValue
        : ''
      : DateInputUtils.localDateTimeToISODateTime(rawValue);

    const finalValue = persistValue === '' ? null : persistValue;

    if (finalValue === cellValue) {
      onCancel();
      return;
    }

    onCommit(finalValue, { moveSelection: moveDirection, validate: true });
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (finishedRef.current) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      finishedRef.current = true;
      onCancel();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      finishedRef.current = true;
      commitValueWithMove(event.shiftKey ? 'UP' : 'DOWN');
      return;
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    if (finishedRef.current) {
      return;
    }

    finishedRef.current = true;
    commitValue();
  };

  const handleChange = (): void => {
    if (finishedRef.current) {
      return;
    }

    finishedRef.current = true;
    commitValue();
  };

  if (shouldAutoInsert) {
    return null;
  }

  return (
    <input
      ref={inputRef}
      type={inputType}
      className={styles.input}
      data-testid="date-cell-editor"
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  );
};
